import _ from "lodash";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import classes from "./TableRow.scss";
import { observeElementInViewport } from "observe-element-in-viewport";
import { Draggable } from "react-beautiful-dnd";
import classNames from "classnames";
import {
  onAddNewNode,
  updateNode,
  pasteBulkBenchmarkItems,
  onUpdateNodeIcon,
  columnIds,
  filterIds,
  onLeftIndentNode,
  onLeftIndentGrades,
  onRightIndentNode,
  onRightIndentGrades,
  onUpdateNodeGrades,
  onAddTags,
  isAnyFilterSelected,
  MANUAL_SCROLL,
  getIsLeftIndentationDisabled,
  getIsRightIndentationDisabled,
} from "GenericTableEditor/GenericTableEditorUtils";
import { SINGLE_LEVEL, MULTI_LEVEL } from "Fields/FieldUtils";
import { useI18n } from "Hooks";

import LabelCell from "./LabelCell";
import SelectableLabelCell from "./SelectableLabelCell";
import LabelCellAsFirstColumn from "./LabelCellAsFirstColumn";
import GradesCell from "./GradesCell";
import TagCell from "./TagCell";
import IconCell from "./IconCell";

import { scrollIntoView } from "Utils";

const TableRow = props => {
  const {
    nodes,
    setNodes,
    nodeId,
    mode,
    maxDepth,
    indexCountArray,
    currentEditorConfig,
    isDraggingOver,
    draggingOverWith,
    gradeOptions,
    tagOptions,
    createNewTags,
    filters,
    setNodesRef,
    scrollBehaviourRef,
    setSelectedSidebarNodes,
    isNodeTagged,
    showTagCheckbox,
    onClickTagCheckbox,
    tableContainerRef,
  } = props;
  const [isRowVisible, setIsRowVisible] = useState(
    // for view, we should have all the rows visible by default.
    mode === "view" ? true : false
  );
  const maxNodeDepthForSidebar =
    currentEditorConfig.maxNodeDepthForSidebar ?? 0;

  const node = nodes[nodeId];

  const { t } = useI18n(["common"]);

  useEffect(() => {
    const target = document.getElementById(nodeId);

    // handler for when target is in viewport
    const inHandler = (entry, unobserve) => {
      if (
        scrollBehaviourRef.current === MANUAL_SCROLL &&
        node.depth <= maxNodeDepthForSidebar
      ) {
        setSelectedSidebarNodes([nodeId]);
      }
      if (!isRowVisible) {
        setIsRowVisible(true);
      } else {
        unobserve();
      }
    };
    // handler for when target is NOT in viewport
    const outHandler = () => {};

    // the returned function, when called, stops tracking the target element in the given viewport
    const unobserveFn = observeElementInViewport(
      target,
      inHandler,
      outHandler,
      {
        // set viewport, if null then it's set to window
        viewport: null,
      }
    );

    return () => {
      unobserveFn();
    };
  }, []);

  const {
    label,
    depth,
    isError = mode === "view" && _.isEmpty(label),
    subText,
    icon = {},
    code,
    grades,
    tags,
  } = node;

  const isLabelEmpty = _.isEmpty(label);

  //calculate index of the node
  const isPresent = _.findIndex(indexCountArray, id => id == nodeId);
  if (isPresent == -1) {
    indexCountArray.push(nodeId);
  }
  const indexOfNode = _.findIndex(indexCountArray, id => id == nodeId);
  //checking if the node is disabled for drag drop. if yes, make a light gray background
  const draggingOverWithId = isDraggingOver && draggingOverWith;

  const isDisabled = draggingOverWithId
    ? node.depth > nodes[draggingOverWithId].depth
    : false;
  //give styling
  const rowContainerStyle = classNames({
    [classes.rowContainer]: true,
    [classes.rowContainerActive]: isNodeTagged || node.isChecked,
    [classes.rowContainerDisabled]: isDisabled,
    [classes.isRowDragging]: draggingOverWithId
      ? draggingOverWithId == nodeId
      : false,
    [classes.isRowError]: isError,
  });

  //functions related to actions

  const isGradeFilterActive = !_.isEmpty(_.get(filters, filterIds.grades, []));

  const isTagsFilterActive = !_.isEmpty(_.get(filters, filterIds.tags, []));

  //4) Add new row
  const onAddRow = useCallback(() => {
    const [updatedNodes, newNodeId] = onAddNewNode({
      nodes,
      setNodes,
      nodeId,
      maxDepth,
      isGradeFilterActive,
      isTagsFilterActive,
    });

    setTimeout(() => {
      scrollIntoView({
        element: document.getElementById(newNodeId),
        checkForElementInViewPort: true,
        block: "center",
      });
    }, 300);
  }, [
    nodes,
    setNodes,
    maxDepth,
    nodeId,
    isGradeFilterActive,
    isTagsFilterActive,
  ]);

  //5) on checkbox click
  const onCheckboxClick = useCallback(
    ({ prevIsChecked }) => {
      setNodes(prevNodes => {
        return {
          ...prevNodes,
          [nodeId]: { ...prevNodes[nodeId], isChecked: !prevIsChecked },
        };
      });
    },
    [setNodes, nodeId]
  );

  const removeError = useCallback(() => {
    if (isError) {
      setNodes(prevNodes => {
        return {
          ...prevNodes,
          [nodeId]: {
            ...prevNodes[nodeId],
            isError: false,
          },
        };
      });
    }
  }, [isError, nodeId, setNodes]);

  //1) Content edit
  const onContentEdit = useCallback(
    ({ params }) => {
      updateNode({ nodeId, setNodes, params });
      //remove any error state for this node.
      removeError();
    },
    [nodeId, setNodes, removeError]
  );

  const onUpdateIcon = useCallback(
    ({ icon }) => {
      onUpdateNodeIcon({
        setNodes,
        nodeId,
        icon,
        nodes,
      });

      //remove the error state for this node.
      removeError();
    },
    [setNodes, nodeId, nodes, removeError]
  );

  //8) Bulk rows that are copied from google docs excel sheet are being paste into NodeEditor.js.
  const onBulkPasteRows = useCallback(
    ({ pastedData, cellName, isNewRow, itemsList }) => {
      pasteBulkBenchmarkItems({
        nodes,
        setNodes,
        nodeId,
        pastedData,
        cellName,
        isNewRow,
        itemsList,
        isGradeFilterActive,
        isTagsFilterActive,
      });
    },
    [nodes, setNodes, nodeId, isGradeFilterActive, isTagsFilterActive]
  );

  //allowed columns
  const allowedColumns = currentEditorConfig.columns;

  const isGradeColumnPresent = useMemo(
    () =>
      _.findIndex(allowedColumns, column => {
        return column.id == columnIds.grades;
      }) > -1,
    [allowedColumns]
  );

  const onLeftIndent = useCallback(() => {
    const updatedNodes = onLeftIndentNode({
      nodes,
      setNodes,
      nodeId,
      maxDepth,
    });
    if (_.size(updatedNodes) > 0 && isGradeColumnPresent) {
      onLeftIndentGrades({
        nodes: updatedNodes,
        setNodes,
        nodeId,
      });
    }
  }, [nodes, setNodes, nodeId, maxDepth, isGradeColumnPresent]);

  //3) Right Indent

  const onRightIndent = useCallback(() => {
    const updatedNodes = onRightIndentNode({
      nodes,
      setNodes,
      nodeId,
    });

    if (_.size(updatedNodes) > 0 && isGradeColumnPresent) {
      onRightIndentGrades({
        nodes: updatedNodes,
        setNodes,
        nodeId,
      });
    }
  }, [nodes, setNodes, nodeId, maxDepth, isGradeColumnPresent]);

  const onGradesUpdate = useCallback(
    ({ grades, nodeGrades }) => {
      onUpdateNodeGrades({
        setNodes,
        nodeId,
        grades,
        nodes,
        nodeGrades,
      });

      removeError();
    },
    [setNodes, nodeId, nodes, removeError]
  );

  const onAddTagsLocal = useCallback(
    ({ itemsList }) => {
      onAddTags({
        nodes,
        setNodes,
        selectedNodeIds: [nodeId],
        itemsList,
      });
    },
    [nodes, setNodes, nodeId]
  );

  const areFiltersActive = useMemo(() => {
    return isAnyFilterSelected({ filters });
  }, [filters]);

  return (
    <Draggable
      key={nodeId}
      draggableId={nodeId}
      index={indexOfNode}
      isDragDisabled={mode === "view" || areFiltersActive}
    >
      {provided => (
        <tr
          className={rowContainerStyle}
          ref={ref => {
            provided.innerRef(ref);
            depth <= maxNodeDepthForSidebar
              ? setNodesRef({ id: nodeId, ref })
              : null;
          }}
          {...provided.draggableProps}
          id={nodeId}
        >
          {_.map(allowedColumns, (column, index) => {
            const columnStyle = _.get(column, "style", {});

            const placeholderTexts = _.get(column, "placeholderTexts", []);

            switch (column.id) {
              case columnIds.label: {
                const customAddIconTooltipLabel = _.get(
                  column,
                  "customAddIconTooltipLabel",
                  ""
                );

                const columnVariant = _.get(column, "variant", SINGLE_LEVEL);

                const isIndentationAllowed = columnVariant === MULTI_LEVEL;

                const isLeftIndentationDisabled = isIndentationAllowed
                  ? getIsLeftIndentationDisabled({
                      nodes,
                      nodeId,
                      areFiltersActive,
                    })
                  : null;

                const isRightIndentationDisabled = isIndentationAllowed
                  ? getIsRightIndentationDisabled({
                      nodes,
                      nodeId,
                      maxDepth,
                      areFiltersActive,
                    })
                  : null;

                return (
                  <td
                    className={classes.tableCell}
                    key={column.id}
                    style={columnStyle}
                  >
                    <LabelCellAsFirstColumn
                      renderChildren={() => (
                        <LabelCell
                          label={label}
                          placeholderText={t("common:enter_with_label", {
                            label: t(placeholderTexts[0]),
                          })}
                          cellName="label"
                          isMandatory={true}
                          onContentEdit={onContentEdit}
                          onBulkPasteRows={onBulkPasteRows}
                          shouldConsiderNewRowOnPaste={true}
                          removeError={removeError}
                          isError={isError}
                          depth={depth}
                          variant={columnVariant}
                          searchText={_.get(filters, filterIds.searchText, "")}
                        />
                      )}
                      isError={isError}
                      onAddRow={onAddRow}
                      areFiltersActive={areFiltersActive}
                      isAddButtonDisabled={
                        !_.isEmpty(_.get(filters, filterIds.searchText, ""))
                      }
                      addIconTooltipText={customAddIconTooltipLabel}
                      currentEditorConfig={currentEditorConfig}
                      node={node}
                      onCheckboxClick={onCheckboxClick}
                      dragHandleProps={provided.dragHandleProps}
                      isIndentationAllowed={isIndentationAllowed}
                      onLeftIndent={onLeftIndent}
                      onRightIndent={onRightIndent}
                      isLeftIndentationDisabled={isLeftIndentationDisabled}
                      isRightIndentationDisabled={isRightIndentationDisabled}
                    />
                  </td>
                );
              }
              case columnIds.selectableLabel: {
                const columnVariant = _.get(column, "variant", SINGLE_LEVEL);

                return (
                  <td
                    className={classes.tableCell}
                    key={column.id}
                    style={columnStyle}
                  >
                    <SelectableLabelCell
                      label={label}
                      depth={depth}
                      variant={columnVariant}
                      searchText={_.get(filters, filterIds.searchText, "")}
                      isSelected={isNodeTagged}
                      showCheckbox={showTagCheckbox}
                      truncateText={_.get(column, "truncateText", false)}
                      onClickCheckbox={e => {
                        onClickTagCheckbox({
                          id: node.id,
                          shouldSelectRange: e.shiftKey,
                        });
                      }}
                    />
                  </td>
                );
              }
              case columnIds.previewSelectableLabel: {
                const columnVariant = _.get(column, "variant", SINGLE_LEVEL);
                const finalLabel =
                  mode === "view" && isLabelEmpty
                    ? t("common:label_not_filled", {
                        label: t("common:list_item_one"),
                      })
                    : label;

                return (
                  <td
                    className={classes.tableCell}
                    key={column.id}
                    style={columnStyle}
                  >
                    <SelectableLabelCell
                      label={finalLabel}
                      isLabelEmpty={isLabelEmpty}
                      depth={depth}
                      variant={columnVariant}
                      searchText={_.get(filters, filterIds.searchText, "")}
                      isSelected={isNodeTagged}
                      disableCheckbox
                      showCheckbox={showTagCheckbox}
                      onClickCheckbox={_.noop}
                    />
                  </td>
                );
              }

              case columnIds.descriptor: {
                const isMandatory = _.includes(
                  _.get(currentEditorConfig, "errorCheckFields", []),
                  "subText"
                );

                /**
                 * For shouldConsiderNewRowOnPaste prop:
                 * Here we are making assumption that when this column is not the first column,
                 * user will paste data in earlier columns and then paste data in this column. Otherwise, it won't function correctly.
                 */
                return isRowVisible ? (
                  <td
                    className={classes.tableCell}
                    key={column.id}
                    style={columnStyle}
                  >
                    <LabelCell
                      label={subText}
                      placeholderText={t("common:enter_with_label", {
                        label: t(placeholderTexts[0]),
                      })}
                      cellName="subText"
                      onContentEdit={onContentEdit}
                      onBulkPasteRows={onBulkPasteRows}
                      shouldConsiderNewRowOnPaste={index === 0}
                      removeError={removeError}
                      isMandatory={isMandatory}
                      isError={isMandatory ? isError : false}
                    />
                  </td>
                ) : null;
              }
              case columnIds.icon: {
                return isRowVisible ? (
                  <td
                    className={classes.tableCell}
                    key={column.id}
                    style={columnStyle}
                  >
                    <IconCell
                      icon={icon}
                      onUpdateIcon={onUpdateIcon}
                      containerParentRef={tableContainerRef}
                    />
                  </td>
                ) : null;
              }
              case columnIds.grades: {
                return isRowVisible ? (
                  <td
                    className={classes.tableCell}
                    key={column.id}
                    style={columnStyle}
                  >
                    <GradesCell
                      gradeOptions={gradeOptions}
                      grades={grades}
                      mode={mode}
                      onUpdateGrades={onGradesUpdate}
                    />
                  </td>
                ) : null;
              }

              case columnIds.tags: {
                return isRowVisible ? (
                  <td
                    className={classes.tableCell}
                    key={column.id}
                    style={columnStyle}
                  >
                    <TagCell
                      mode={mode}
                      tags={tags}
                      tagOptions={tagOptions}
                      createNewTags={createNewTags}
                      onAddTags={onAddTagsLocal}
                    />
                  </td>
                ) : null;
              }

              case columnIds.code: {
                const isMandatory = _.includes(
                  _.get(currentEditorConfig, "errorCheckFields", []),
                  "code"
                );

                const isCodeEmpty = _.isEmpty(code);

                return isRowVisible ? (
                  <td
                    className={classes.tableCell}
                    key={column.id}
                    style={columnStyle}
                  >
                    <LabelCell
                      label={mode === "view" && isCodeEmpty ? "--" : code}
                      placeholderText={t(placeholderTexts[0])}
                      cellName="code"
                      onContentEdit={onContentEdit}
                      onBulkPasteRows={onBulkPasteRows}
                      shouldConsiderNewRowOnPaste={index === 0}
                      removeError={removeError}
                      isMandatory={isMandatory}
                      isError={isMandatory ? isError : false}
                      isEditable={mode === "edit"}
                    />
                  </td>
                ) : null;
              }
            }
          })}
        </tr>
      )}
    </Draggable>
  );
};

const checkProps = (prevProps, nextProps) => {
  const avoidRerender =
    prevProps.isNodeTagged == nextProps.isNodeTagged &&
    prevProps.nodes == nextProps.nodes &&
    prevProps.setNodes == nextProps.setNodes &&
    prevProps.rootNodeId == nextProps.rootNodeId &&
    prevProps.mode == nextProps.mode &&
    prevProps.maxDepth == nextProps.maxDepth &&
    JSON.stringify(prevProps.currentEditorConfig) ==
      JSON.stringify(nextProps.currentEditorConfig) &&
    prevProps.hasChildren == nextProps.hasChildren &&
    prevProps.isDraggingOver == nextProps.isDraggingOver &&
    prevProps.draggingOverWith == nextProps.draggingOverWith &&
    prevProps.filters == nextProps.filters;

  return avoidRerender;
};

export default React.memo(TableRow, checkProps);
